import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
  ScrollView,
  Image,
  Alert,
  Dimensions,
  Keyboard,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/contexts/AuthContext';
import { getUserProfilePhotoUrl } from '../../lib/services/auth';
import { DynamicTitle } from '../DynamicTitle';
import { setComensalCiudadPreferida, type CiudadListaItem } from '../../lib/services/comensal-ciudad';
import type { EdicionActivaBanner } from '../../lib/services/comensal-edicion';
import { edicionCatalogoPath } from '../../lib/utils/edicionNombreLink';
import {
  fetchCiudadesListaCached,
  fetchEdicionEscaparateComensalCached,
  getPropuestasByEdicionIdCached,
  type Propuesta,
} from '../../lib/cache/comensalReadCache';
import { propuestasService } from '../../lib/services/propuestas';
import { getImageDisplayUrl } from '../../lib/utils/imageTransform';
import { urlFotoMostrar } from '../../lib/utils/propuestaFotos';
import {
  rankPropuestasPorDistancia,
  rankRestaurantesFichaPorDistancia,
  type PropuestaConDistancia,
  type RestauranteCercano,
} from '../../lib/utils/propuestaDistancia';
import { getRestaurantes } from '../../database/database';
import { getCurrentUserPosition } from '../../lib/utils/userGeolocation';
import { findNearestCiudad } from '../../lib/utils/nearestCiudad';
import { listPropuestaFavoritaIdsPorUsuario } from '../../lib/services/propuestas-favoritas';
import { PropuestaFavoritoButton } from './PropuestaFavoritoButton';
import { COMENSAL_TAB_BAR_OVERLAY_INSET } from './ComensalBottomTabBar';

/** 5:00–11:59 Buenos días, 12:00–18:59 Buenas tardes, 19:00–4:59 Buenas noches */
function greetingByHour(d: Date = new Date()): string {
  const h = d.getHours();
  if (h >= 5 && h < 12) return 'Buenos días';
  if (h >= 12 && h < 19) return 'Buenas tardes';
  return 'Buenas noches';
}

function firstNameForGreeting(profile: { nombre?: string | null; email?: string | null }): string {
  const raw = (profile.nombre || '').trim();
  if (raw) {
    const first = raw.split(/\s+/)[0];
    return first || raw;
  }
  const mail = (profile.email || '').trim();
  if (mail) {
    const local = mail.split('@')[0];
    return local || 'comensal';
  }
  return 'comensal';
}

function initialsFromDisplayName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`.toUpperCase();
  }
  if (parts.length === 1 && parts[0].length >= 2) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  if (parts.length === 1 && parts[0].length === 1) {
    return parts[0].toUpperCase();
  }
  return '?';
}

function parseLocalDayStart(iso: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (!y || !mo || !d) return null;
  const dt = new Date(y, mo - 1, d, 0, 0, 0, 0);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

/** Fin del día de cierre (inclusive), hora local. */
function parseLocalDayEnd(iso: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (!y || !mo || !d) return null;
  const dt = new Date(y, mo - 1, d, 23, 59, 59, 999);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function pluralEs(n: number, singular: string, plural: string): string {
  return n === 1 ? singular : plural;
}

/** "X días y Y horas" respecto a `ms` restantes (entero > 0). */
function formatDiasYHorasRestantes(ms: number): string {
  const days = Math.floor(ms / 86_400_000);
  let hours = Math.floor((ms % 86_400_000) / 3_600_000);
  if (days === 0 && hours === 0 && ms > 0) {
    hours = Math.max(1, Math.ceil(ms / 3_600_000));
  }
  const dPart = `${days} ${pluralEs(days, 'día', 'días')}`;
  const hPart = `${hours} ${pluralEs(hours, 'hora', 'horas')}`;
  return `${dPart} y ${hPart}`;
}

/**
 * Antes del inicio: "Comienza en…". Durante la edición: "Termina en…".
 * Después del cierre: null.
 */
function computeEdicionBannerCountdownLabel(isoInicio: string, isoFin: string, now: Date): string | null {
  const a = isoInicio?.trim();
  const b = isoFin?.trim();
  if (!a || !b) return null;
  const start = parseLocalDayStart(a);
  const end = parseLocalDayEnd(b);
  if (!start || !end || end < start) return null;
  if (now < start) {
    const ms = start.getTime() - now.getTime();
    if (ms <= 0) return null;
    return `Comienza en ${formatDiasYHorasRestantes(ms)}`;
  }
  if (now <= end) {
    const ms = end.getTime() - now.getTime();
    if (ms <= 0) return null;
    return `Termina en ${formatDiasYHorasRestantes(ms)}`;
  }
  return null;
}

function formatDistanciaKmEs(km: number): string {
  if (km < 1) {
    const m = Math.round(km * 1000);
    return `${m} m`;
  }
  return `${km.toLocaleString('es-AR', { maximumFractionDigits: 1, minimumFractionDigits: km < 10 ? 1 : 0 })} km`;
}

/** Solo medios de la propuesta (no logo/fachada del restaurante). */
function propuestaListaImagenUrl(p: Propuesta): string | null {
  const fotos = p.fotos;
  const idx = p.foto_seleccionada_index ?? 0;
  if (fotos?.length) {
    const foto = fotos[idx] ?? fotos[0];
    if (foto?.url) return urlFotoMostrar(foto);
  }
  if (p.foto_restaurante?.trim()) return p.foto_restaurante.trim();
  return null;
}

const PROPUESTA_DESC_PREVIEW_MAX = 110;

function propuestaDescripcionResumen(raw: string | undefined): string {
  if (!raw?.trim()) return '';
  const t = raw.replace(/\s+/g, ' ').trim();
  if (t.length <= PROPUESTA_DESC_PREVIEW_MAX) return t;
  return `${t.slice(0, PROPUESTA_DESC_PREVIEW_MAX - 1)}…`;
}

const PROPUESTAS_CERCANAS_MAX = 12;
const RESTAURANTES_CERCANOS_VISTA = 4;

export function ComensalAppHome() {
  const navigate = useNavigate();
  const { user, profile, loading, refreshProfile } = useAuth();
  const [avatarError, setAvatarError] = useState(false);
  const [ciudadesList, setCiudadesList] = useState<CiudadListaItem[]>([]);
  const [loadingCiudades, setLoadingCiudades] = useState(false);
  const [ciudadesError, setCiudadesError] = useState<string | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [savingCiudad, setSavingCiudad] = useState(false);
  const [headerBlockHeight, setHeaderBlockHeight] = useState(52);
  /** Ciudad más cercana según GPS mientras se guarda en perfil (evita "Tu ciudad" hasta que llegue refresh). */
  const [pendingNearestCiudad, setPendingNearestCiudad] = useState<{ id: number; nombre: string } | null>(null);
  const autoCiudadInFlight = useRef(false);
  /** Se incrementa al elegir ciudad en el desplegable; evita que la geolocalización en curso pise la elección. */
  const manualCiudadPickNonce = useRef(0);
  const [edicionBanner, setEdicionBanner] = useState<EdicionActivaBanner | null>(null);
  /** Recalcula el contador del banner ~cada minuto. */
  const [edicionBannerNowTick, setEdicionBannerNowTick] = useState(0);
  const [edicionActivaId, setEdicionActivaId] = useState<number | null>(null);
  const [mapCoords, setMapCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [mapCoordsReady, setMapCoordsReady] = useState(false);
  const [propuestasCercanas, setPropuestasCercanas] = useState<PropuestaConDistancia[]>([]);
  const [loadingPropuestasCercanas, setLoadingPropuestasCercanas] = useState(false);
  const [restaurantesCercanos, setRestaurantesCercanos] = useState<RestauranteCercano[]>([]);
  const [hayMasRestaurantesCercanos, setHayMasRestaurantesCercanos] = useState(false);
  const [loadingRestaurantesCercanos, setLoadingRestaurantesCercanos] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [favoritasIds, setFavoritasIds] = useState<Set<string>>(() => new Set());

  const handleSubmitSearch = () => {
    const q = searchQuery.trim();
    if (!q) return;
    Keyboard.dismiss();
    // Pendiente: enlazar con resultados (restaurante, propuesta, categoría…)
  };

  useEffect(() => {
    setAvatarError(false);
  }, [profile?.foto_url, profile?.avatar_url, profile?.id]);

  useEffect(() => {
    let cancelled = false;
    if (!user?.id) {
      setFavoritasIds(new Set());
      return;
    }
    void listPropuestaFavoritaIdsPorUsuario(user.id)
      .then((ids) => {
        if (!cancelled) setFavoritasIds(ids);
      })
      .catch(() => {
        if (!cancelled) setFavoritasIds(new Set());
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  useEffect(() => {
    let cancelled = false;
    fetchEdicionEscaparateComensalCached()
      .then(({ id, banner }) => {
        if (!cancelled) {
          setEdicionBanner(banner);
          setEdicionActivaId(id);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setEdicionBanner(null);
          setEdicionActivaId(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!edicionBanner) return;
    const id = setInterval(() => setEdicionBannerNowTick((n) => n + 1), 60_000);
    return () => clearInterval(id);
  }, [edicionBanner?.id]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    setLoadingCiudades(true);
    setCiudadesError(null);
    fetchCiudadesListaCached()
      .then((list) => {
        if (!cancelled) setCiudadesList(list);
      })
      .catch((e: any) => {
        if (!cancelled) {
          setCiudadesError(e?.message ?? 'No se pudieron cargar las ciudades');
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingCiudades(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    setMapCoordsReady(false);
    getCurrentUserPosition()
      .then((p) => {
        if (!cancelled) setMapCoords({ lat: p.latitude, lng: p.longitude });
      })
      .catch(() => {
        if (!cancelled) setMapCoords(null);
      })
      .finally(() => {
        if (!cancelled) setMapCoordsReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const edicionIdParaPropuestas = edicionBanner?.id ?? edicionActivaId;

  const edicionCatalogoAppPath = useMemo(() => {
    if (edicionIdParaPropuestas == null) return null;
    return edicionCatalogoPath(
      {
        id: edicionIdParaPropuestas,
        nombre_link:
          edicionBanner?.id === edicionIdParaPropuestas ? edicionBanner.nombre_link : null,
      },
      { app: true }
    );
  }, [edicionIdParaPropuestas, edicionBanner]);

  useEffect(() => {
    if (!edicionIdParaPropuestas || !mapCoords) {
      setPropuestasCercanas([]);
      setLoadingPropuestasCercanas(false);
      return;
    }
    let cancelled = false;
    setLoadingPropuestasCercanas(true);
    getPropuestasByEdicionIdCached(String(edicionIdParaPropuestas))
      .then((list) => {
        if (cancelled) return;
        const rankedFull = rankPropuestasPorDistancia(list, mapCoords.lat, mapCoords.lng);
        setPropuestasCercanas(rankedFull.slice(0, PROPUESTAS_CERCANAS_MAX));
      })
      .catch(() => {
        if (!cancelled) setPropuestasCercanas([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingPropuestasCercanas(false);
      });
    return () => {
      cancelled = true;
    };
  }, [edicionIdParaPropuestas, mapCoords]);

  const ciudadesIdsParaRestaurantes = useMemo(() => {
    const ids = profile?.ciudades_ids?.filter(Boolean) ?? [];
    if (ids.length > 0) return ids;
    if (pendingNearestCiudad?.id != null) return [String(pendingNearestCiudad.id)];
    return [];
  }, [profile?.ciudades_ids, pendingNearestCiudad]);

  useEffect(() => {
    if (!mapCoords || ciudadesIdsParaRestaurantes.length === 0) {
      setRestaurantesCercanos([]);
      setHayMasRestaurantesCercanos(false);
      setLoadingRestaurantesCercanos(false);
      return;
    }
    let cancelled = false;
    setLoadingRestaurantesCercanos(true);
    getRestaurantes(ciudadesIdsParaRestaurantes)
      .then(async (list) => {
        if (cancelled) return;
        const ranked = rankRestaurantesFichaPorDistancia(list, mapCoords.lat, mapCoords.lng);
        const linkMap = await propuestasService.mapPrimeraPropuestaPublicaPorRestauranteIds(
          ranked.map((x) => x.restaurante.id),
          edicionIdParaPropuestas ?? null
        );
        if (cancelled) return;
        const enriched: RestauranteCercano[] = ranked.map(({ restaurante, distanceKm }) => {
          const link = linkMap.get(restaurante.id);
          return {
            restaurante,
            distanceKm,
            propuestaEjemploId: link?.propuestaId,
            edicionEjemploId: link?.edicionId,
          };
        });
        setRestaurantesCercanos(enriched.slice(0, RESTAURANTES_CERCANOS_VISTA));
        setHayMasRestaurantesCercanos(enriched.length > RESTAURANTES_CERCANOS_VISTA);
      })
      .catch(() => {
        if (!cancelled) {
          setRestaurantesCercanos([]);
          setHayMasRestaurantesCercanos(false);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingRestaurantesCercanos(false);
      });
    return () => {
      cancelled = true;
    };
  }, [mapCoords, ciudadesIdsParaRestaurantes, edicionIdParaPropuestas]);

  useEffect(() => {
    autoCiudadInFlight.current = false;
    setPendingNearestCiudad(null);
  }, [user?.id]);

  useEffect(() => {
    if (profile?.ciudades_ids?.length) {
      setPendingNearestCiudad(null);
    }
  }, [profile?.ciudades_ids]);

  // Cada carga de /app (o cuando entra usuario + lista de ciudades): ciudad por defecto = la más cercana por GPS.
  // No incluimos profile.ciudades_ids en dependencias para no pisar una selección manual hecha en la misma visita.
  useEffect(() => {
    if (!user?.id || !profile?.id) return;
    if (ciudadesList.length === 0) return;

    const withCoords = ciudadesList.filter(
      (c) =>
        c.latitud_centro != null &&
        c.longitud_centro != null &&
        !Number.isNaN(c.latitud_centro) &&
        !Number.isNaN(c.longitud_centro)
    );
    if (withCoords.length === 0) return;
    if (autoCiudadInFlight.current) return;
    autoCiudadInFlight.current = true;

    const perfilCiudadAlInicio = profile.ciudades_ids?.[0] ?? null;
    const nonceAtRun = manualCiudadPickNonce.current;
    let cancelled = false;

    (async () => {
      try {
        const pos = await getCurrentUserPosition();
        if (cancelled) return;
        const nearest = findNearestCiudad(pos.latitude, pos.longitude, withCoords);
        if (!nearest) return;

        if (perfilCiudadAlInicio != null && String(nearest.ciudad.id) === String(perfilCiudadAlInicio)) {
          return;
        }

        if (manualCiudadPickNonce.current !== nonceAtRun) return;

        setPendingNearestCiudad({ id: nearest.ciudad.id, nombre: nearest.ciudad.nombre });
        if (cancelled) return;
        if (manualCiudadPickNonce.current !== nonceAtRun) return;
        await setComensalCiudadPreferida(user.id, nearest.ciudad.id);
        if (cancelled) return;
        await refreshProfile({ silent: true });
      } catch {
        if (!cancelled) setPendingNearestCiudad(null);
        // Sin permiso GPS u otro error: se mantiene la ciudad del perfil si la había
      } finally {
        if (!cancelled) autoCiudadInFlight.current = false;
      }
    })();

    return () => {
      cancelled = true;
      autoCiudadInFlight.current = false;
    };
    // refreshProfile: ver comentario anterior (no memoizado en AuthContext)
  }, [user?.id, profile?.id, ciudadesList]);

  const dropdownPanelWidth = useMemo(() => {
    const screenW = Dimensions.get('window').width;
    const cap = Math.min(screenW - 40, screenW * 0.92);
    const labels = ciudadesList.map((c) => c.nombre);
    if (labels.length === 0) {
      return Math.min(168, cap);
    }
    const longest = labels.reduce((a, b) => (a.length >= b.length ? a : b), '');
    const estimated = Math.ceil(longest.length * 8.5 + 56);
    return Math.min(Math.max(estimated, 120), cap);
  }, [ciudadesList]);

  const edicionBannerImageUri = useMemo(() => {
    if (!edicionBanner?.imageUrl) return null;
    return (
      getImageDisplayUrl(edicionBanner.imageUrl, { preset: 'cover' }) || edicionBanner.imageUrl
    );
  }, [edicionBanner]);

  const edicionBannerCountdownLabel = useMemo(() => {
    if (!edicionBanner) return null;
    return computeEdicionBannerCountdownLabel(
      edicionBanner.fechaInicio,
      edicionBanner.fechaFin,
      new Date()
    );
  }, [edicionBanner, edicionBannerNowTick]);

  if (loading) {
    return (
      <View style={styles.centered}>
        <ActivityIndicator size="large" color="#030213" />
      </View>
    );
  }

  if (!user || !profile) {
    return null;
  }

  const handleCiudadSelect = async (ciudadId: number) => {
    const actual = profile.ciudades_ids?.[0];
    if (actual != null && String(ciudadId) === String(actual)) {
      setDropdownOpen(false);
      return;
    }
    manualCiudadPickNonce.current += 1;
    setSavingCiudad(true);
    try {
      await setComensalCiudadPreferida(user.id, ciudadId);
      await refreshProfile({ silent: true });
      setDropdownOpen(false);
    } catch (e: any) {
      Alert.alert('Error', e?.message ?? 'No se pudo guardar la ciudad');
    } finally {
      setSavingCiudad(false);
    }
  };

  const displayName = [profile.nombre, profile.apellido].filter(Boolean).join(' ').trim() || profile.email;
  const avatarUri = getUserProfilePhotoUrl(profile);
  const showAvatarImage = Boolean(avatarUri && !avatarError);
  const initials = initialsFromDisplayName(displayName);

  const nombresCiudad = profile.ciudades_nombres?.filter(Boolean) ?? [];
  let ciudadBoton: string;
  if (nombresCiudad.length === 0) {
    ciudadBoton = pendingNearestCiudad?.nombre ?? 'Tu ciudad';
  } else if (nombresCiudad.length === 1) {
    ciudadBoton = nombresCiudad[0];
  } else {
    ciudadBoton = `${nombresCiudad[0]} +${nombresCiudad.length - 1}`;
  }

  const ciudadSeleccionadaId =
    profile.ciudades_ids?.[0] ?? (pendingNearestCiudad ? String(pendingNearestCiudad.id) : null);

  return (
    <View style={styles.screen}>
      <DynamicTitle title="Inicio — As Gastronómico" />
      <View style={[styles.topBarOuter, dropdownOpen && styles.topBarOuterOpen]}>
        <View
          style={styles.headerMeasured}
          onLayout={(e) => setHeaderBlockHeight(e.nativeEvent.layout.height)}
        >
          <View style={styles.topBar}>
            <View style={styles.avatarWrap} accessibilityLabel="Foto de perfil">
              {showAvatarImage ? (
                <Image
                  source={{ uri: avatarUri as string }}
                  style={styles.avatarImage}
                  resizeMode="cover"
                  onError={() => setAvatarError(true)}
                />
              ) : (
                <View style={styles.avatarFallback}>
                  <Text style={styles.avatarInitials}>{initials}</Text>
                </View>
              )}
            </View>
            <View style={styles.topBarSpacer} />
            <TouchableOpacity
              style={[styles.ciudadButton, savingCiudad && styles.ciudadButtonDisabled]}
              onPress={() => {
                if (!savingCiudad) setDropdownOpen((o) => !o);
              }}
              activeOpacity={0.75}
              disabled={savingCiudad}
              accessibilityLabel={`Ciudad: ${ciudadBoton}. Abrir listado`}
              accessibilityState={{ expanded: dropdownOpen }}
            >
              <Ionicons name="location-outline" size={18} color="#030213" style={styles.ciudadIcon} />
              {savingCiudad ? (
                <ActivityIndicator size="small" color="#030213" style={styles.ciudadSaving} />
              ) : (
                <Text style={styles.ciudadButtonText} numberOfLines={1} ellipsizeMode="tail">
                  {ciudadBoton}
                </Text>
              )}
              <Ionicons
                name={dropdownOpen ? 'chevron-up' : 'chevron-down'}
                size={18}
                color="#6b7280"
                style={styles.ciudadChevron}
              />
            </TouchableOpacity>
          </View>

          {ciudadesError ? (
            <Text style={styles.ciudadesErrorText}>{ciudadesError}</Text>
          ) : null}
        </View>

        {dropdownOpen && !savingCiudad ? (
          <View
            style={[
              styles.dropdownPanel,
              {
                width: dropdownPanelWidth,
                top: headerBlockHeight + 8,
              },
            ]}
          >
            {loadingCiudades ? (
              <View style={styles.dropdownLoading}>
                <ActivityIndicator size="small" color="#030213" />
              </View>
            ) : ciudadesList.length === 0 ? (
              <Text style={styles.dropdownEmpty}>No hay ciudades disponibles</Text>
            ) : (
              <ScrollView
                style={styles.dropdownScroll}
                nestedScrollEnabled
                keyboardShouldPersistTaps="handled"
                showsVerticalScrollIndicator
              >
                {ciudadesList.map((c) => {
                  const selected =
                    ciudadSeleccionadaId != null && String(c.id) === String(ciudadSeleccionadaId);
                  return (
                    <TouchableOpacity
                      key={c.id}
                      style={[styles.dropdownItem, selected && styles.dropdownItemSelected]}
                      onPress={() => handleCiudadSelect(c.id)}
                      activeOpacity={0.65}
                    >
                      <Text style={[styles.dropdownItemText, selected && styles.dropdownItemTextSelected]}>
                        {c.nombre}
                      </Text>
                      {selected ? (
                        <Ionicons name="checkmark-circle" size={20} color="#030213" />
                      ) : null}
                    </TouchableOpacity>
                  );
                })}
              </ScrollView>
            )}
          </View>
        ) : null}
      </View>

      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.hero}>
          <Text style={styles.greeting}>
            {greetingByHour()}, {firstNameForGreeting(profile)}
          </Text>
          <Text style={styles.tagline}>¿Qué quieres comer hoy?</Text>
          <View style={styles.searchRow}>
            <View style={styles.searchFieldWrap}>
              <Ionicons name="search-outline" size={20} color="#9ca3af" style={styles.searchIcon} />
              <TextInput
                style={styles.searchInput}
                value={searchQuery}
                onChangeText={setSearchQuery}
                placeholder="Restaurante, propuesta, categoría…"
                placeholderTextColor="#9ca3af"
                returnKeyType="search"
                autoCorrect={false}
                autoCapitalize="none"
                clearButtonMode="while-editing"
                onSubmitEditing={handleSubmitSearch}
                accessibilityLabel="Buscar por restaurante, propuesta o categoría"
              />
            </View>
            <TouchableOpacity
              style={styles.searchButton}
              onPress={handleSubmitSearch}
              activeOpacity={0.85}
              accessibilityLabel="Buscar"
              accessibilityRole="button"
            >
              <Ionicons name="search" size={22} color="#ffffff" />
            </TouchableOpacity>
          </View>

          {edicionBanner && edicionBannerImageUri ? (
            <TouchableOpacity
              style={styles.edicionBannerWrap}
              onPress={() =>
                navigate(
                  edicionCatalogoPath(
                    { id: edicionBanner.id, nombre_link: edicionBanner.nombre_link },
                    { app: true }
                  )
                )
              }
              activeOpacity={0.9}
              accessibilityRole="button"
              accessibilityLabel={[`Edición ${edicionBanner.nombre}`, edicionBannerCountdownLabel]
                .filter(Boolean)
                .join('. ')}
            >
              <View style={styles.edicionBannerInner}>
                <Image
                  source={{ uri: edicionBannerImageUri }}
                  style={styles.edicionBannerImage}
                  resizeMode="cover"
                  accessibilityIgnoresInvertColors
                />
                <View style={styles.edicionBannerOverlay}>
                  <Text style={styles.edicionBannerTitle} numberOfLines={2}>
                    {edicionBanner.nombre}
                  </Text>
                  {edicionBannerCountdownLabel ? (
                    <Text style={styles.edicionBannerDates} numberOfLines={2}>
                      {edicionBannerCountdownLabel}
                    </Text>
                  ) : null}
                </View>
              </View>
            </TouchableOpacity>
          ) : null}
        </View>

        {edicionIdParaPropuestas ? (
          <>
            <View style={styles.cercanasSection}>
              <Text style={styles.cercanasTitle}>Propuestas cercanas</Text>
              {!mapCoordsReady ? (
                <View style={styles.cercanasLoadingRow}>
                  <ActivityIndicator size="small" color="#030213" />
                  <Text style={styles.cercanasHint}>Obteniendo tu ubicación…</Text>
                </View>
              ) : !mapCoords ? (
                <Text style={styles.cercanasHint}>
                  Activa el permiso de ubicación para ver propuestas ordenadas por distancia.
                </Text>
              ) : loadingPropuestasCercanas && propuestasCercanas.length === 0 ? (
                <View style={styles.cercanasLoadingRow}>
                  <ActivityIndicator size="small" color="#030213" />
                </View>
              ) : propuestasCercanas.length === 0 ? (
                <Text style={styles.cercanasHint}>
                  No hay propuestas con ubicación disponible en esta edición.
                </Text>
              ) : (
                <ScrollView
                  horizontal
                  showsHorizontalScrollIndicator={false}
                  nestedScrollEnabled
                  contentContainerStyle={styles.cercanasCarousel}
                >
                  {propuestasCercanas.map(({ propuesta, distanceKm, ubicacionCorta }) => {
                    const img = propuestaListaImagenUrl(propuesta);
                    const uri = img
                      ? getImageDisplayUrl(img, { preset: 'card' }) || img.replace(/^http:/, 'https:')
                      : null;
                    const restNombre =
                      propuesta.restaurante?.nombre_mostrar ||
                      propuesta.restaurante?.nombre_oficial ||
                      '';
                    const descPreview = propuestaDescripcionResumen(propuesta.descripcion);
                    const a11yUbic = ubicacionCorta ? `. ${ubicacionCorta}` : '';
                    const a11yDesc = descPreview ? `. ${descPreview}` : '';
                    return (
                      <TouchableOpacity
                        key={propuesta.id}
                        style={styles.cercanaCard}
                        activeOpacity={0.88}
                        onPress={() =>
                          edicionCatalogoAppPath &&
                          navigate(`${edicionCatalogoAppPath}#propuesta-${propuesta.id}`)
                        }
                        accessibilityRole="button"
                        accessibilityLabel={`${propuesta.titulo}. ${restNombre}${a11yDesc}${a11yUbic}. A ${formatDistanciaKmEs(distanceKm)}`}
                      >
                        <View style={styles.cercanaImageWrap}>
                          {uri ? (
                            <Image source={{ uri }} style={styles.cercanaImage} resizeMode="cover" />
                          ) : (
                            <View style={[styles.cercanaImage, styles.cercanaImagePlaceholder]}>
                              <Ionicons name="restaurant-outline" size={32} color="#9ca3af" />
                            </View>
                          )}
                          <PropuestaFavoritoButton
                            propuestaId={propuesta.id}
                            favorita={favoritasIds.has(propuesta.id)}
                            onChange={(next) => {
                              setFavoritasIds((prev) => {
                                const n = new Set(prev);
                                if (next) n.add(propuesta.id);
                                else n.delete(propuesta.id);
                                return n;
                              });
                            }}
                            style={styles.cercanaFavoritoBtn}
                            size={20}
                          />
                        </View>
                        <View style={styles.cercanaBody}>
                          <Text style={styles.cercanaTitulo} numberOfLines={2}>
                            {propuesta.titulo}
                          </Text>
                          {restNombre ? (
                            <Text style={styles.cercanaRest} numberOfLines={1}>
                              {restNombre}
                            </Text>
                          ) : null}
                          {descPreview ? (
                            <Text style={styles.cercanaDesc} numberOfLines={3}>
                              {descPreview}
                            </Text>
                          ) : null}
                          <View style={styles.cercanaUbicRow}>
                            <Ionicons
                              name="location-outline"
                              size={14}
                              color="#6b7280"
                              style={styles.cercanaUbicIcon}
                            />
                            <Text style={styles.cercanaUbicText} numberOfLines={2}>
                              {ubicacionCorta}
                            </Text>
                          </View>
                          <Text style={styles.cercanaDist}>{formatDistanciaKmEs(distanceKm)}</Text>
                        </View>
                      </TouchableOpacity>
                    );
                  })}
                </ScrollView>
              )}
            </View>
          </>
        ) : null}

        <View style={styles.cercanasSection}>
          <View style={styles.sectionTitleRow}>
            <Text style={[styles.cercanasTitle, styles.cercanasTitleInRow]}>Restaurantes cercanos</Text>
            {ciudadesIdsParaRestaurantes.length > 0 &&
            mapCoordsReady &&
            mapCoords &&
            !loadingRestaurantesCercanos &&
            hayMasRestaurantesCercanos ? (
              <TouchableOpacity
                onPress={() =>
                  navigate('/restaurantes-cercanos', {
                    state: { ciudadIds: ciudadesIdsParaRestaurantes },
                  })
                }
                hitSlop={8}
                accessibilityRole="button"
                accessibilityLabel="Ver todos los restaurantes cercanos"
              >
                <Text style={styles.verTodosText}>Ver todos</Text>
              </TouchableOpacity>
            ) : null}
          </View>
          {ciudadesIdsParaRestaurantes.length === 0 ? (
            <Text style={styles.cercanasHint}>
              Elige tu ciudad arriba para ver restaurantes ordenados por distancia en tu zona.
            </Text>
          ) : !mapCoordsReady ? (
            <View style={styles.cercanasLoadingRow}>
              <ActivityIndicator size="small" color="#030213" />
              <Text style={styles.cercanasHint}>Obteniendo tu ubicación…</Text>
            </View>
          ) : !mapCoords ? (
            <Text style={styles.cercanasHint}>
              Activa el permiso de ubicación para ver restaurantes ordenados por distancia.
            </Text>
          ) : loadingRestaurantesCercanos && restaurantesCercanos.length === 0 ? (
            <View style={styles.cercanasLoadingRow}>
              <ActivityIndicator size="small" color="#030213" />
            </View>
          ) : restaurantesCercanos.length === 0 ? (
            <Text style={styles.cercanasHint}>
              No hay restaurantes con ubicación en {ciudadBoton}.
            </Text>
          ) : (
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              nestedScrollEnabled
              contentContainerStyle={styles.cercanasCarousel}
            >
              {restaurantesCercanos.map((item) => {
                const img = item.restaurante.logo?.trim() || null;
                const uri = img
                  ? getImageDisplayUrl(img, { preset: 'card' }) || img.replace(/^http:/, 'https:')
                  : null;
                const nombre =
                  item.restaurante.nombre_mostrar?.trim() ||
                  item.restaurante.nombre_oficial?.trim() ||
                  '';
                const openFicha = () => {
                  if (item.propuestaEjemploId && item.edicionEjemploId != null) {
                    navigate(
                      `${edicionCatalogoPath({ id: item.edicionEjemploId, nombre_link: null }, { app: true })}#propuesta-${item.propuestaEjemploId}`
                    );
                  }
                };
                return (
                  <TouchableOpacity
                    key={item.restaurante.id}
                    style={styles.cercanaCard}
                    activeOpacity={0.88}
                    onPress={openFicha}
                    disabled={!item.propuestaEjemploId || item.edicionEjemploId == null}
                    accessibilityRole="button"
                    accessibilityLabel={`${nombre}, a ${formatDistanciaKmEs(item.distanceKm)}`}
                  >
                    {uri ? (
                      <Image source={{ uri }} style={styles.cercanaImage} resizeMode="cover" />
                    ) : (
                      <View style={[styles.cercanaImage, styles.cercanaImagePlaceholder]}>
                        <Ionicons name="restaurant-outline" size={32} color="#9ca3af" />
                      </View>
                    )}
                    <View style={styles.cercanaBody}>
                      <Text style={styles.cercanaTitulo} numberOfLines={2}>
                        {nombre}
                      </Text>
                      {item.restaurante.ciudad_nombre ? (
                        <Text style={styles.cercanaRest} numberOfLines={1}>
                          {item.restaurante.ciudad_nombre}
                        </Text>
                      ) : null}
                      <Text style={styles.cercanaDist}>{formatDistanciaKmEs(item.distanceKm)}</Text>
                    </View>
                  </TouchableOpacity>
                );
              })}
            </ScrollView>
          )}
        </View>

        <Text style={styles.footerNote}>Área comensal · As Gastronómico</Text>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: '#f9fafb',
  },
  scroll: {
    flex: 1,
    zIndex: 0,
  },
  scrollContent: {
    paddingHorizontal: 20,
    paddingTop: 0,
    paddingBottom: 24 + COMENSAL_TAB_BAR_OVERLAY_INSET,
  },
  centered: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#ffffff',
  },
  topBarOuter: {
    position: 'relative',
    zIndex: 1,
    marginBottom: 20,
    paddingHorizontal: 20,
    paddingTop: 16,
    overflow: 'visible',
  },
  topBarOuterOpen: {
    zIndex: 200,
  },
  headerMeasured: {
    width: '100%',
  },
  topBar: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  topBarSpacer: {
    flex: 1,
    minWidth: 10,
  },
  avatarWrap: {
    width: 44,
    height: 44,
    borderRadius: 22,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: '#e5e7eb',
    backgroundColor: '#f3f4f6',
    flexShrink: 0,
  },
  ciudadButton: {
    flexDirection: 'row',
    alignItems: 'center',
    maxWidth: '72%',
    flexShrink: 1,
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderRadius: 999,
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  ciudadButtonDisabled: {
    opacity: 0.75,
  },
  ciudadIcon: {
    marginRight: 6,
    flexShrink: 0,
  },
  ciudadButtonText: {
    flex: 1,
    flexShrink: 1,
    minWidth: 0,
    fontSize: 14,
    fontWeight: '600',
    color: '#030213',
  },
  ciudadSaving: {
    flex: 1,
    alignSelf: 'center',
  },
  ciudadChevron: {
    marginLeft: 4,
    flexShrink: 0,
  },
  ciudadesErrorText: {
    fontSize: 12,
    color: '#dc2626',
    marginTop: 8,
  },
  dropdownPanel: {
    position: 'absolute',
    right: 0,
    /* top dinámico: headerBlockHeight + 8 */
    /* ~3 filas visibles; el resto hace scroll */
    maxHeight: 158,
    backgroundColor: '#ffffff',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.12,
    shadowRadius: 12,
    elevation: 12,
    overflow: 'hidden',
    zIndex: 300,
  },
  dropdownScroll: {
    maxHeight: 154,
  },
  dropdownLoading: {
    paddingVertical: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  dropdownEmpty: {
    padding: 16,
    fontSize: 14,
    color: '#6b7280',
    textAlign: 'center',
  },
  dropdownItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    paddingVertical: 12,
    paddingHorizontal: 14,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
  },
  dropdownItemSelected: {
    backgroundColor: '#f9fafb',
  },
  dropdownItemText: {
    flexShrink: 1,
    fontSize: 15,
    color: '#030213',
  },
  dropdownItemTextSelected: {
    fontWeight: '600',
  },
  avatarImage: {
    width: '100%',
    height: '100%',
  },
  avatarFallback: {
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#e5e7eb',
  },
  avatarInitials: {
    fontSize: 15,
    fontWeight: '700',
    color: '#030213',
  },
  hero: {
    marginBottom: 20,
  },
  greeting: {
    fontSize: 16,
    color: '#6b7280',
    lineHeight: 22,
  },
  tagline: {
    fontSize: 16,
    fontWeight: '700',
    color: '#030213',
    marginTop: 6,
    lineHeight: 22,
  },
  searchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 14,
    gap: 10,
  },
  searchFieldWrap: {
    flex: 1,
    flexShrink: 1,
    minWidth: 0,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderRadius: 12,
    paddingHorizontal: 12,
    minHeight: 48,
  },
  searchIcon: {
    marginRight: 8,
    flexShrink: 0,
  },
  searchInput: {
    flex: 1,
    flexShrink: 1,
    minWidth: 0,
    fontSize: 16,
    color: '#030213',
    paddingVertical: 12,
    paddingRight: 4,
  },
  searchButton: {
    flexShrink: 0,
    backgroundColor: '#030213',
    minWidth: 48,
    minHeight: 48,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 14,
  },
  edicionBannerWrap: {
    marginTop: 16,
    borderRadius: 14,
    overflow: 'hidden',
    backgroundColor: '#e5e7eb',
    shadowColor: '#030213',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 3,
  },
  edicionBannerInner: {
    position: 'relative',
    width: '100%',
  },
  edicionBannerImage: {
    width: '100%',
    aspectRatio: 16 / 9,
  },
  edicionBannerOverlay: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    paddingHorizontal: 14,
    paddingTop: 28,
    paddingBottom: 12,
    backgroundColor: 'rgba(3, 2, 19, 0.78)',
  },
  edicionBannerTitle: {
    fontSize: 17,
    fontWeight: '700',
    color: '#ffffff',
    lineHeight: 22,
  },
  edicionBannerDates: {
    marginTop: 6,
    fontSize: 13,
    color: 'rgba(255, 255, 255, 0.92)',
    lineHeight: 18,
  },
  cercanasSection: {
    marginTop: 8,
    marginBottom: 4,
  },
  cercanasTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#030213',
    marginBottom: 12,
  },
  sectionTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    marginBottom: 12,
  },
  cercanasTitleInRow: {
    flex: 1,
    marginBottom: 0,
  },
  verTodosText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#030213',
  },
  cercanasLoadingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 8,
  },
  cercanasHint: {
    fontSize: 14,
    color: '#6b7280',
    lineHeight: 20,
  },
  cercanasCarousel: {
    flexDirection: 'row',
    gap: 12,
    paddingBottom: 4,
  },
  cercanaCard: {
    width: 220,
    backgroundColor: '#ffffff',
    borderRadius: 14,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    overflow: 'hidden',
    shadowColor: '#030213',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.06,
    shadowRadius: 6,
    elevation: 2,
  },
  cercanaImageWrap: {
    position: 'relative',
  },
  cercanaFavoritoBtn: {
    position: 'absolute',
    top: 8,
    right: 8,
    zIndex: 2,
    width: 32,
    height: 32,
    borderRadius: 16,
  },
  cercanaImage: {
    width: '100%',
    height: 120,
    backgroundColor: '#f3f4f6',
  },
  cercanaImagePlaceholder: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  cercanaBody: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    gap: 4,
  },
  cercanaTitulo: {
    fontSize: 15,
    fontWeight: '600',
    color: '#030213',
    lineHeight: 20,
  },
  cercanaRest: {
    fontSize: 13,
    color: '#6b7280',
  },
  cercanaDesc: {
    fontSize: 12,
    color: '#6b7280',
    lineHeight: 16,
    marginTop: 2,
  },
  cercanaUbicRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 6,
    marginTop: 4,
  },
  cercanaUbicIcon: {
    marginTop: 2,
    flexShrink: 0,
  },
  cercanaUbicText: {
    flex: 1,
    minWidth: 0,
    fontSize: 12,
    color: '#4b5563',
    lineHeight: 16,
    fontWeight: '500',
  },
  cercanaDist: {
    fontSize: 13,
    fontWeight: '600',
    color: '#030213',
    marginTop: 4,
  },
  footerNote: {
    marginTop: 20,
    fontSize: 12,
    color: '#9ca3af',
    textAlign: 'center',
  },
});
